import { CustomerExecutionReportsBlotterTable } from '@src/containers/Blotters/CustomerExecutionReports/CustomerExecutionReportsBlotter';
import { CustomerQuotesBlotterTable } from '@src/containers/Blotters/CustomerQuotes/CustomerQuotesBlotter';
import { ExecutionReportsBlotterTable } from '@src/containers/Blotters/ExecutionReports';
import { MarketExecutionReportsBlotterTable } from '@src/containers/Blotters/MarketExecutionReports';
import { MarketOrdersBlotterTable } from '@src/containers/Blotters/MarketOrders';
import { MarketQuotesBlotterTable } from '@src/containers/Blotters/MarketQuotes/MarketQuotesBlotter';
import { FilteredOrders } from '@src/containers/Blotters/Orders/FilteredOrders';
import { QuotesBlotterTable } from '@src/containers/Blotters/Quotes/QuotesBlotter';
import { FilteredTrades } from '@src/containers/Blotters/Trades/FilteredTrades';
import { DEFAULT_TRADE_COLUMNS } from '@src/containers/Blotters/Trades/columns';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import {
  AccordionGroup,
  AssetTransaction,
  type BlotterTableFilter,
  type Column,
  ExecutionReport,
  MarketExecutionReport,
  MarketOrder,
  MarketQuote,
  MixpanelEventSource,
  MixpanelSourceProvider,
  VStack,
} from '@talos/kyoko';
import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { isEmpty } from 'lodash-es';
import { type FC, useMemo } from 'react';
import { AssetTransactionsBlotterTable } from '../../Blotters/AssetTransactions/AssetTransactionsBlotter';
import { BuyingPowerBlotterTable } from '../../Blotters/BuyingPower/BuyingPowerBlotter';
import { CareExecutionReportsBlotterTable } from '../../Blotters/CareExecutionReports/CareExecutionReportsBlotter';
import { CareOrderBlotter } from '../../Blotters/CareOrders/CareOrderBlotter';
import { CustomerAssetTransactionsBlotterTable } from '../../Blotters/CustomerAssetTransactions/CustomerAssetTransactionsBlotterTable';
import { CustomerOrdersBlotterTable } from '../../Blotters/CustomerOrders/CustomerOrdersBlotter';
import { CustomerTradesBlotterTable } from '../../Blotters/CustomerTrades/CustomerTradesBlotterTable';
import { FixingIndicesBlotterTable } from '../../Blotters/FixingIndices/FixingIndicesBlotterTable';
import { IndexMarksBlotterTable } from '../../Blotters/IndexMarks/IndexMarksBlotterTable';
import { MarketTradesBlotterTable } from '../../Blotters/MarketTrades';
import { NotificationsBlotterTable } from '../../Blotters/Notifications/NotifiationsBlotterTable';
import { type BlotterTableEntity, entityByRequestName } from './types';

export interface MonitoringBlotterProps<T extends BlotterTableEntity = BlotterTableEntity> {
  blotterID: string;
  tabLabel?: string;
  onRowDoubleClicked(event: RowDoubleClickedEvent<T>): void;
  initialIsOpen?: boolean;
  selectedEntity: keyof typeof entityByRequestName;
  onCloneTab: () => void;
  defaultColumns?: (string | Column)[];
  defaultFilter?: BlotterTableFilter;
  generateOrderDetailsRoute: GenerateOrderDetailsRoute;
  source?: 'monitoring' | 'dealer-monitoring';
  layoutId?: string;
}

interface GenericBlotterProps {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: any[];
  defaultFilter?: BlotterTableFilter;
  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<any>) => void;
  initialIsOpen?: boolean;
  showAPIOrdersToggle?: boolean;
  source?: 'monitoring' | 'dealer-monitoring';
}

const BlotterMixpanelEventSourceLookup = {
  ExecutionReport: MixpanelEventSource.MonitoringExecutionReportsBlotter,
  Order: MixpanelEventSource.MonitoringOrdersBlotter,
  Quote: MixpanelEventSource.MonitoringQuotesBlotter,
  Trade: MixpanelEventSource.MonitoringTradesBlotter,
  CustomerExecutionReport: MixpanelEventSource.MonitoringCustomerExecutionReportsBlotter,
  CustomerOrder: MixpanelEventSource.MonitoringCustomerOrdersBlotter,
  CustomerQuote: MixpanelEventSource.MonitoringCustomerQuotesBlotter,
  CustomerTrade: MixpanelEventSource.MonitoringCustomerTradesBlotter,
  CustomerAssetTransaction: MixpanelEventSource.MonitoringMarketCashEventsBlotter,
  MarketExecutionReport: MixpanelEventSource.MonitoringMarketExecutionReportsBlotter,
  MarketOrder: MixpanelEventSource.MonitoringMarketOrdersBlotter,
  MarketQuote: MixpanelEventSource.MonitoringMarketQuotesBlotter,
  MarketTrade: MixpanelEventSource.MonitoringMarketTradesBlotter,
  AssetTransaction: MixpanelEventSource.MonitoringMarketCashEventsBlotter,
  BuyingPower: MixpanelEventSource.BuyingPowerBlotter,
  CareOrder: MixpanelEventSource.CareOrderBlotter,
  CareExecutionReport: MixpanelEventSource.CareExecutionReportsBlotter,
  Index: MixpanelEventSource.MonitoringIndexBlotter,
  IndexMark: MixpanelEventSource.MonitoringIndexMarkBlotter,
  Notification: MixpanelEventSource.MonitoringNotificationsBlotter,
} as const satisfies Record<keyof typeof entityByRequestName, MixpanelEventSource>;

export function MonitoringBlotter<T extends BlotterTableEntity = BlotterTableEntity>(props: MonitoringBlotterProps<T>) {
  const selectedEntity = props.selectedEntity ?? Object.keys(entityByRequestName)[0];

  const SelectedBlotter: FC<GenericBlotterProps> = useMemo<FC<GenericBlotterProps>>((): any => {
    switch (selectedEntity) {
      case 'Order':
        return FilteredOrders;
      case 'Trade':
        return FilteredTrades;
      case 'ExecutionReport':
        return ExecutionReportsBlotterTable;
      case 'MarketOrder':
        return MarketOrdersBlotterTable;
      case 'MarketExecutionReport':
        return MarketExecutionReportsBlotterTable;
      case 'Quote':
        return QuotesBlotterTable;
      case 'MarketQuote':
        return MarketQuotesBlotterTable;
      case 'MarketTrade':
        return MarketTradesBlotterTable;
      case 'AssetTransaction':
        return AssetTransactionsBlotterTable;
      case 'CustomerOrder':
        return CustomerOrdersBlotterTable;
      case 'CustomerQuote':
        return CustomerQuotesBlotterTable;
      case 'CustomerTrade':
        return CustomerTradesBlotterTable;
      case 'CustomerAssetTransaction':
        return CustomerAssetTransactionsBlotterTable;
      case 'CustomerExecutionReport':
        return CustomerExecutionReportsBlotterTable;
      case 'BuyingPower':
        return BuyingPowerBlotterTable;
      case 'CareOrder':
        return CareOrderBlotter;
      case 'CareExecutionReport':
        return CareExecutionReportsBlotterTable;
      case 'Index':
        return FixingIndicesBlotterTable;
      case 'IndexMark':
        return IndexMarksBlotterTable;
      case 'Notification':
        return NotificationsBlotterTable;
      default: {
        const _exhaustiveCheck: never = selectedEntity;
      }
    }
  }, [selectedEntity]);

  const mixpanelEventSource: MixpanelEventSource = useMemo<MixpanelEventSource>(() => {
    return BlotterMixpanelEventSourceLookup[selectedEntity];
  }, [selectedEntity]);

  const defaultColumns: GenericBlotterProps['defaultColumns'] = useMemo(() => {
    if (!isEmpty(props.defaultColumns)) {
      return props.defaultColumns;
    }
    switch (selectedEntity) {
      case 'Trade':
        return DEFAULT_TRADE_COLUMNS;
      case 'ExecutionReport':
        return ExecutionReport.defaultColumns;
      case 'MarketOrder':
        return MarketOrder.defaultColumns;
      case 'MarketExecutionReport':
        return MarketExecutionReport.defaultColumns;
      case 'MarketQuote':
        return MarketQuote.defaultColumns;
      case 'AssetTransaction':
        return AssetTransaction.defaultColumns;
      case 'Quote':
      case 'Order':
      case 'CustomerQuote':
      case 'CustomerExecutionReport':
      case 'CustomerOrder':
      case 'CustomerTrade':
      case 'CustomerAssetTransaction':
      case 'MarketTrade':
      case 'BuyingPower':
      case 'CareOrder':
      case 'CareExecutionReport':
      case 'Index':
      case 'IndexMark':
      case 'Notification':
        return []; // use the defaults defined by the blotter
      default:
        throw new Error(`Unknown entity ${selectedEntity}`);
    }
  }, [props.defaultColumns, selectedEntity]) as Column[];

  return (
    <VStack w="100%" h="100%" alignItems="stretch" justifyContent="flex-start">
      <MixpanelSourceProvider value={mixpanelEventSource}>
        <AccordionGroup>
          <SelectedBlotter {...props} defaultColumns={defaultColumns} showAPIOrdersToggle={true} />
        </AccordionGroup>
      </MixpanelSourceProvider>
    </VStack>
  );
}
